import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from './api.service';
import { MulticastObserverFactoryService } from './multicast-observer-factory.service';
import { BehaviorSubject, Observable, distinctUntilChanged, switchMap } from 'rxjs';
import { Town } from '../../shared/entities/town';
import { TranslateService } from '@ngx-translate/core';
import { SkihirePoiResponseInterface } from '../../shared/interfaces/skihire-poi-response-interface';
import { TownNotFoundInterface } from '../../shared/interfaces/town-not-found-interface';

@Injectable({
  providedIn: 'root'
})
export class TownService {
  private town: Town;
  private slug: string;
  public town$: Observable<Town>;
  readonly salesLocationNotFound = new EventEmitter<TownNotFoundInterface>();
  readonly onTownUpdate = new EventEmitter<Town>();

  private slug$ = new BehaviorSubject<string>('');

  constructor(
    private apiService: ApiService,
    multicastObservableFactory: MulticastObserverFactoryService,
    private translateService: TranslateService
  ) {
    this.town$ = multicastObservableFactory.create<Town>(this.loadTownData);
  }

  getTownForSlug(slug: string) {
    if (this.isNewTown(slug)) {
      this.slug = slug;
      this.town = null;
      this.slug$.next(slug);
    }
    this.town$.subscribe(town => {
      if (town) {
        this.onTownUpdate.emit(town);
      }
    });
  }

  private loadTownData = (observer?) => {
    // if we have a town and the town matches the given slug, return it
    if (this.town && !this.isNewTown(this.slug)) {
      observer.next(this.town);
      observer.complete();
    } else {
      // if we have a slug, on with a switchmap that keeps all observers up to date
      if (this.slug) {
        this.slug$
          .pipe(
            distinctUntilChanged(),
            switchMap(slug => {
              return this.apiService.getTownBySlug(slug.split('/').slice(0, 3).join('/'));
            })
          )
          .subscribe({
            next: data => {
              // response is not a town. TODO: fix api response to be jsonApi Response compatible
              if (
                Object.prototype.hasOwnProperty.call(data, 'code') ||
                Object.prototype.hasOwnProperty.call(data, 'error')
              ) {
                this.townNotFound(data);
                observer.complete();
              } else {
                // town found, all ok. send response.
                this.town = new Town(data);
                observer.next(this.town);
                observer.complete();
              }
            },
            error: () => {
              console.error('town for slug: ' + this.slug + ' not found');
            }
          });
      } else {
        observer.next();
        observer.complete();
      }
    }
  };

  private isNewTown(slug: string): boolean {
    if (this.town) {
      for (const lang in this.town.townSlugs) {
        if (this.town.getSlugByCulture(lang).localeCompare(slug) === 0) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * handles the case when a town wasn't found.
   * It builds a saleslocationNotFound object and returns it with the salesLocationNotFound emitter
   * @param slug a bad request slug
   */
  private townNotFound(data) {
    const parts = this.slug.split('/');
    const search = parts[parts.length - 1];
    this.apiService
      .getSkihirePois(search, this.translateService.currentLang)
      .subscribe((result: SkihirePoiResponseInterface) => {
        this.salesLocationNotFound.emit({
          code: data.code,
          slug: search,
          strict: result.strict,
          similar: result.similar
        });
      });
  }
}
